import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import SearchIcon from '@mui/icons-material/Search';
import {
  adminRegionsService,
  categoriesService,
} from '../../shared/services/catalog.service';
import { providersService } from '../../shared/services/providers.service';
import { CityCard } from '../../shared/components/ui/CityCard';
// Geoapify is the single source of truth for city autocomplete across
// the whole app (LocationPicker + this search bar). The previous mix
// of backend cities + Photon fallback has been retired in favour of
// {@link useCityAutocomplete}.
import { useCityAutocomplete } from '../../shared/hooks/useCityAutocomplete';
import type { GeoapifyCity } from '../../shared/services/geoapify.service';
import {
  CURATED_CATEGORIES,
  getFeaturedCategories,
  type CategorySlug,
} from '../../shared/assets/categoryImages';
import { useSelectedCountry } from '../../shared/hooks/useSelectedCountry';
import type { AdminRegion, Category } from '../../shared/types';

/**
 * Override MUI's "popper matches input width" default so each location
 * picker's dropdown can show long names like "Provence-Alpes-Côte
 * d'Azur" or "Mecklenburg-Vorpommern" without truncation, even when the
 * search bar is squeezed into 4–5 narrow columns.
 */
const WIDE_DROPDOWN_SLOT_PROPS = {
  popper: {
    // `width: auto` releases the popper from the anchor's width.
    // min/max keep it readable on phones AND from sprawling on desktop.
    sx: { width: 'auto !important', minWidth: 260, maxWidth: 380 },
  },
  paper: {
    sx: { minWidth: 260, maxWidth: 380 },
  },
} as const;

/**
 * Visual wrapper for one search-bar field: label on top, control below.
 * Keeps the three location pickers visually consistent without
 * duplicating the wrapper sx props on each call site.
 */
function PickerCell({
  label,
  flex = 1,
  children,
}: {
  label: string;
  flex?: number;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        flex,
        px: { xs: 2.5, md: 3 },
        py: { xs: 1.5, md: 1.75 },
        minWidth: 0,
      }}
    >
      <Typography
        component="span"
        sx={{
          display: 'block',
          color: 'text.secondary',
          fontSize: '0.8125rem',
          fontWeight: 500,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Borderless TextField used inside each Autocomplete so the field reads
 * as one continuous pill rather than a boxed input.
 */
function PickerInput({
  params,
  id,
  placeholder,
}: {
  params: import('@mui/material').AutocompleteRenderInputParams;
  id: string;
  placeholder: string;
}) {
  return (
    <TextField
      {...params}
      id={id}
      variant="standard"
      placeholder={placeholder}
      InputProps={{
        ...params.InputProps,
        disableUnderline: true,
        sx: {
          fontSize: '1rem',
          fontWeight: 500,
          p: 0,
          '&::before, &::after': { display: 'none' },
        },
      }}
    />
  );
}

/**
 * Normalise a city name into the lowercase slug used both to look up a
 * cover photo (src/app/asset/cities/<slug>.jpg) and to key the dedupe
 * map. Providers seeded from the `cities` table already carry a slug;
 * those onboarded via Geoapify only have a free-text name, so we derive
 * one here — diacritics flattened so "Genève" and "Geneve" collapse.
 */
function citySlug(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function findBackendCategory(
  categories: Category[] | undefined,
  searchTerm: string,
): Category | undefined {
  if (!categories) return undefined;
  const needle = searchTerm.toLowerCase();
  return categories.find(
    (c) => c.code.toLowerCase().includes(needle) || c.name.toLowerCase().includes(needle),
  );
}

export function CategoryLandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: CategorySlug }>();

  const curated = useMemo(
    () => CURATED_CATEGORIES.find((c) => c.slug === slug),
    [slug],
  );

  // Active i18n locale, used to localise country / region names server-side.
  // i18next reports the negotiated language (e.g. "en-US"); we send the
  // 2-letter base so the backend's `country_translations` rows match.
  const locale =
    (i18n.resolvedLanguage ?? i18n.language ?? 'fr').slice(0, 2).toLowerCase();

  // Persisted across the whole app via localStorage — when the visitor
  // changes country here, the navbar pill and any other search bar update
  // too.
  const { countryCode: countryAlpha2 } = useSelectedCountry();
  const [adminRegionId, setAdminRegionId] = useState<string>('');
  /** Picked city name. May come from Geoapify (with coords) or a
   *  free-typed entry (no coords). The persisted lat/lng live alongside
   *  so we can later pass them to the backend for proximity search. */
  const [cityName, setCityName] = useState<string>('');
  const [cityLat, setCityLat] = useState<number | undefined>(undefined);
  const [cityLon, setCityLon] = useState<number | undefined>(undefined);
  const [cityInput, setCityInput] = useState<string>('');

  // Geoapify-powered city suggestions. Debounce + abort live inside the
  // hook (single source of truth across LocationPicker + this bar).
  // Restrict to the currently-selected market so suggestions stay
  // on-topic; locale tracks the active country so place names come back
  // in the local language.
  const cityQuery = useCityAutocomplete(cityInput, {
    countryCodes: countryAlpha2 ? [countryAlpha2] : undefined,
    lang: countryAlpha2 ? countryAlpha2.toLowerCase() : undefined,
  });

  const adminRegionsQ = useQuery({
    queryKey: ['admin-regions', countryAlpha2, locale],
    queryFn: () => adminRegionsService.list(countryAlpha2, locale),
    enabled: Boolean(countryAlpha2),
    staleTime: 60 * 60 * 1000,
  });

  const categories = useQuery({
    queryKey: ['categories', countryAlpha2],
    queryFn: () => categoriesService.list(countryAlpha2 || undefined),
  });

  // Resolve the curated slug → the backend Category row, so the
  // "available cities" lookup below can be scoped to this category.
  // Resolves once the categories list arrives (async).
  const resolvedCategoryId = useMemo(() => {
    if (!curated || !categories.data) return undefined;
    return findBackendCategory(categories.data, curated.searchTerm)?.id;
  }, [categories.data, curated]);

  // ── Cities where this category is already available ─────────
  // Server-side aggregation: every city in the visitor's country with at
  // least one approved provider for this category, busiest first. Lets
  // the landing page show a "where can I book this" grid up front,
  // before the visitor types any search.
  const cityCoverageQ = useQuery({
    queryKey: ['providers', 'category-cities', resolvedCategoryId, countryAlpha2],
    queryFn: () =>
      providersService.citiesForCategory(resolvedCategoryId!, countryAlpha2 || undefined),
    enabled: Boolean(resolvedCategoryId),
  });

  // Reset cascading selections when an upstream value changes so we never
  // end up with a region/city that doesn't belong to the chosen country.
  useEffect(() => {
    setAdminRegionId('');
    setCityName('');
    setCityLat(undefined);
    setCityLon(undefined);
    setCityInput('');
  }, [countryAlpha2]);
  useEffect(() => {
    setCityName('');
    setCityLat(undefined);
    setCityLon(undefined);
    setCityInput('');
  }, [adminRegionId]);

  if (!curated) return <Navigate to="/" replace />;

  const backendCategory = findBackendCategory(categories.data, curated.searchTerm);
  const categoryName = t(curated.i18nKey);

  const selectedRegion =
    adminRegionsQ.data?.find((r) => r.id === adminRegionId) ?? null;

  // Geoapify-only options. When the value the parent persists matches
  // one of the current suggestions we surface the rich object so the
  // chip shows the Bundesland badge; otherwise we fall back to the
  // bare string (resumed drafts, free-typed values).
  const selectedCity: GeoapifyCity | string | null = useMemo(() => {
    if (!cityName) return null;
    const fromResults = cityQuery.results.find((c) => c.city === cityName);
    return fromResults ?? cityName;
  }, [cityQuery.results, cityName]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (cityName) params.set('cityName', cityName);
    // Forward coordinates so the search backend can rank by distance.
    if (cityLat != null) params.set('lat', String(cityLat));
    if (cityLon != null) params.set('lon', String(cityLon));
    if (countryAlpha2) params.set('country', countryAlpha2);
    if (adminRegionId) params.set('adminRegionId', adminRegionId);
    if (backendCategory) params.set('categoryId', backendCategory.id);
    navigate(`/search?${params.toString()}`);
  };

  const handleCategoryChange = (nextSlug: CategorySlug) => {
    navigate(`/category/${nextSlug}`);
  };

  // Jump straight into a search pre-filtered on this category + city —
  // the visitor picked a city card, so they already know where.
  const handleCityClick = (cityName: string) => {
    const params = new URLSearchParams();
    params.set('cityName', cityName);
    if (countryAlpha2) params.set('country', countryAlpha2);
    if (backendCategory) params.set('categoryId', backendCategory.id);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <Box>
      {/* HERO — light, search-focused */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 5, md: 8 },
          backgroundImage: (theme) =>
            theme.palette.mode === 'dark'
              ? `radial-gradient(60% 80% at 20% 0%, ${theme.palette.primary.dark}40 0%, transparent 60%),
                 radial-gradient(50% 80% at 90% 30%, ${theme.palette.secondary.dark}30 0%, transparent 60%)`
              : `radial-gradient(60% 80% at 20% 0%, ${theme.palette.primary.light}26 0%, transparent 60%),
                 radial-gradient(50% 80% at 90% 30%, ${theme.palette.secondary.light}1A 0%, transparent 60%)`,
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Breadcrumbs
            separator={<NavigateNextRoundedIcon fontSize="small" />}
            sx={{ mb: 3, justifyContent: 'center', display: 'flex' }}
          >
            <Link
              component={RouterLink}
              to="/"
              underline="hover"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              <HomeRoundedIcon sx={{ fontSize: '1rem' }} />
              {t('nav.home')}
            </Link>
            <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', fontWeight: 600 }}>
              {categoryName}
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h1"
            sx={{
              textAlign: 'center',
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3.25rem' },
              fontWeight: 800,
              lineHeight: 1.1,
              mb: { xs: 4, md: 5 },
              maxWidth: 820,
              mx: 'auto',
            }}
          >
            {t('category.heroTitle', { category: categoryName })}
          </Typography>

          {/* Search panel */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              backdropFilter: 'blur(8px)',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(31, 27, 23, 0.85)'
                  : 'rgba(255, 255, 255, 0.96)',
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 24px 60px rgba(0,0,0,0.55)'
                  : '0 24px 60px rgba(26,24,22,0.10)',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems="stretch"
              divider={
                <Box
                  sx={{
                    bgcolor: 'divider',
                    width: { xs: '100%', md: '1px' },
                    height: { xs: '1px', md: 'auto' },
                  }}
                />
              }
            >
              {/* What */}
              <FormControl
                variant="standard"
                sx={{
                  flex: 1,
                  px: { xs: 2.5, md: 3 },
                  py: { xs: 1.5, md: 1.75 },
                  '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                    display: 'none',
                  },
                }}
              >
                <InputLabel
                  shrink
                  sx={{
                    position: 'static',
                    transform: 'none',
                    color: 'text.secondary',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    mb: 0.25,
                  }}
                >
                  {t('category.searchWhat')}
                </InputLabel>
                <Select
                  variant="standard"
                  disableUnderline
                  value={curated.slug}
                  onChange={(e) => handleCategoryChange(e.target.value as CategorySlug)}
                  sx={{
                    '& .MuiSelect-select': {
                      pl: 0,
                      pr: 4,
                      py: 0,
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'primary.main',
                    },
                  }}
                >
                  {/* The dropdown only lists the seven main categories; the
                      legacy curated entries remain routable but aren't
                      surfaced here to avoid duplicate-looking options. */}
                  {getFeaturedCategories(countryAlpha2).map((c) => (
                    <MenuItem key={c.slug} value={c.slug}>
                      {t(c.i18nKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Country picker intentionally omitted — the country is
                  chosen once at app startup via the top-bar dialog (see
                  useSelectedCountry) and drives the region + city options
                  below. Showing it here would be a second source of truth
                  for the same state. */}

              {/* Region — always visible, every country has at least one
                  region seeded so the picker is never "missing".  When
                  unselected it reads "All regions" so the user can search
                  the whole country without picking anything. */}
              <PickerCell label={t('search.region')}>
                <Autocomplete<AdminRegion, false, false, false>
                  fullWidth
                  options={adminRegionsQ.data ?? []}
                  loading={adminRegionsQ.isLoading}
                  loadingText={t('common.loading')}
                  noOptionsText={t('search.noRegions')}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={selectedRegion}
                  onChange={(_, value) => setAdminRegionId(value?.id ?? '')}
                  slotProps={WIDE_DROPDOWN_SLOT_PROPS}
                  renderInput={(p) => (
                    <PickerInput
                      params={p}
                      id="category-region-input"
                      placeholder={t('search.regionPlaceholder')}
                    />
                  )}
                />
              </PickerCell>

              {/* City — Geoapify autocomplete (DE-only, debounced +
                  abortable). freeSolo lets the user submit a typed
                  name when the API doesn't surface the commune. */}
              <PickerCell label={t('category.searchWhere')} flex={1.2}>
                <Autocomplete<GeoapifyCity, false, false, true>
                  fullWidth
                  freeSolo
                  clearOnBlur={false}
                  selectOnFocus
                  handleHomeEndKeys
                  options={cityQuery.results}
                  loading={cityQuery.isLoading}
                  loadingText={t('common.loading')}
                  noOptionsText={t('search.cityFreeTextHint')}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.city
                  }
                  isOptionEqualToValue={(a, b) =>
                    typeof a !== 'string' &&
                    typeof b !== 'string' &&
                    a.placeId === b.placeId
                  }
                  value={selectedCity}
                  onChange={(_, value) => {
                    if (value == null) {
                      setCityName('');
                      setCityLat(undefined);
                      setCityLon(undefined);
                      return;
                    }
                    if (typeof value === 'string') {
                      setCityName(value.trim());
                      setCityLat(undefined);
                      setCityLon(undefined);
                      return;
                    }
                    setCityName(value.city);
                    setCityLat(value.latitude);
                    setCityLon(value.longitude);
                  }}
                  onInputChange={(_, input, reason) => {
                    if (reason !== 'input') return;
                    setCityInput(input);
                  }}
                  slotProps={WIDE_DROPDOWN_SLOT_PROPS}
                  // Geoapify already returns ranked, deduped results — skip
                  // MUI's default substring filter so we don't accidentally
                  // hide alt-spelling matches (Giessen → Gießen).
                  filterOptions={(options) => options}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ alignItems: 'flex-start !important' }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ lineHeight: 1.2 }}>
                          {option.city}
                        </Typography>
                        {option.region && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ lineHeight: 1.2 }}
                          >
                            {option.region}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(p) => (
                    <PickerInput
                      params={p}
                      id="category-where-input"
                      placeholder={t('category.searchWherePlaceholder')}
                    />
                  )}
                />
              </PickerCell>

              {/* Search button */}
              <Box
                sx={{
                  p: { xs: 1.5, md: 1 },
                  display: 'flex',
                  alignItems: 'stretch',
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  sx={{
                    flex: 1,
                    minWidth: { md: 130 },
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? theme.palette.primary.main
                        : '#1A1816',
                    color: 'common.white',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    px: 3,
                    py: { xs: 1.25, md: 1.5 },
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? theme.palette.primary.dark
                          : '#000000',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {t('common.search')}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* CITIES COVERAGE
          Every city in the visitor's country where this category already
          has at least one provider, rendered as photo cards. Lets the
          visitor see availability across the whole country at a glance
          and jump into a city-scoped search — no search input needed. */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          {t('category.pickCityTitle')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t('category.citiesSubtitle', {
            category: categoryName,
            defaultValue: 'Villes où des prestataires sont déjà disponibles',
          })}
        </Typography>

        {cityCoverageQ.isLoading || categories.isLoading ? (
          <Grid container spacing={3}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : (cityCoverageQ.data?.length ?? 0) === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">{t('category.noCities')}</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {(cityCoverageQ.data ?? []).map((c) => {
              // Providers onboarded via Geoapify have no seeded slug —
              // derive one so the city still gets a stable cover / key.
              const slug = c.slug || citySlug(c.city);
              return (
                <Grid item xs={6} sm={4} md={3} key={slug || c.city}>
                  <CityCard
                    city={{
                      id: slug || c.city,
                      name: c.city,
                      country: c.country,
                      slug,
                    }}
                    overline={t('search.results', { count: c.providerCount })}
                    onClick={() => handleCityClick(c.city)}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
